import type { BevollmaechtigteProfil } from './bevollmaechtigte';
import type { FillField } from './bafaFormFill';
import { splitName } from './bafaFormFill';
import type { VnFormData } from './vorgangApi';
import { computeElster } from './elster';
import { ESF_ALL_QUESTIONS } from './esfFragebogen';

export interface VnConsultant extends BevollmaechtigteProfil {
  contactPerson?: string;
  advisorNumber?: string;
}

/** Backend liefert Geldbeträge als "3.500,00 €" (formatEuro in vn-formdaten.service.ts).
 *  Das BAFA-Formular akzeptiert nur Ziffern mit Komma-Dezimaltrennzeichen, ohne
 *  Tausenderpunkt und ohne Währungszeichen ("3500,00" statt "3.500,00 €") — sonst weist
 *  es das Feld mit "8 Vorkomma- und 2 Nachkommastellen erlaubt" zurück. */
function stripEuroFormat(value: string | null): string | null {
  if (value == null) return null;
  return value.replace(/\./g, '').replace(/\s*€\s*$/, '').trim();
}

/** Alle Felder für Seite 1 des BAFA-Verwendungsnachweis-Formulars, in der Reihenfolge,
 *  in der das echte Formular sie abfragt. Der Radio „Ja, manuell" steht bewusst zuerst —
 *  er schaltet die restlichen Felder auf der Seite erst frei. Die Angaben zur
 *  antragstellenden Person übernimmt BAFA automatisch vom verknüpften Antrag, deshalb
 *  bleiben sie hier außen vor. Kontoinhabende Person und die Erklärungs-Checkboxen
 *  bleiben ebenfalls absichtlich außen vor (manuell auszufüllen). */
export function buildVnPage1Felder(consultant: VnConsultant | null, formData: VnFormData): FillField[] {
  const { vorname: beraterVorname, nachname: beraterNachname } = splitName(consultant?.contactPerson);

  const radioAendern: FillField = { label: 'Ja, manuell', value: 'x', kind: 'radio' };

  const beratungsunternehmenFelder: FillField[] = [
    consultant?.advisorNumber
      ? { label: 'BAFA-ID', value: 'x', kind: 'radio' as const }
      : { label: 'Keine BAFA-ID bekannt', value: 'x', kind: 'radio' as const },
    { label: 'BAFA-ID', value: consultant?.advisorNumber },
    { label: 'Unternehmen', value: consultant?.companyName },
  ];

  const durchfuehrendePersonFelder: FillField[] = [
    { label: 'Vorname', value: beraterVorname || undefined },
    { label: 'Nachname', value: beraterNachname || undefined },
  ];

  const beratungFelder: FillField[] = [
    { label: 'Beratungsbereich', value: formData.beratung.bereich },
    { label: 'Beratungsbeginn', value: formData.beratung.beginn },
    { label: 'Beratungsende', value: formData.beratung.ende },
    { label: 'Anzahl der Beratungstage', value: formData.beratung.tage != null ? String(formData.beratung.tage) : null },
    { label: 'Inhalt und Ziel des Beratungsauftrages', value: formData.beratung.inhaltZiel },
  ];

  const kostenFelder: FillField[] = [
    { label: 'Kosten ohne USt.', value: stripEuroFormat(formData.kosten.netto) },
    { label: 'Kosten mit USt.', value: stripEuroFormat(formData.kosten.mitUst) },
  ];

  const bankFelder: FillField[] = [
    { label: 'IBAN', value: formData.bank.iban },
  ];

  // Die zuwendungsempfangendenPerson.personTyp-Radios ("natürliche"/"juristische Person")
  // haben im echten BAFA-Formular KEIN <label>-Element (per Diagnose bestätigt) — Matching
  // über Options-Text funktioniert hier also nicht, deshalb direkt per bekannter DOM-id.
  // Rechtsform wird trotzdem aus den übrigen unternehmen-Einträgen entfernt, sonst würde
  // ihr Text ("natürliche Person"/"juristische Person") fälschlich als Textfeld behandelt.
  const { Rechtsform: _rechtsform, ...restUnternehmen } = formData.unternehmen;
  const personTypRadio: FillField = formData.natuerlichePerson
    ? { label: 'natürliche Person', value: 'x', kind: 'radio' as const, id: 'personTyp-NATUERLICHE_PERSON' }
    : { label: 'juristische Person', value: 'x', kind: 'radio' as const, id: 'personTyp-JURISTISCHE_PERSON' };
  const personentypFelder: FillField[] = [
    personTypRadio,
    ...Object.entries(restUnternehmen).map(([label, value]) => ({ label, value })),
    ...(!formData.natuerlichePerson
      ? [{ label: 'Steuernummer (ELSTER)', value: computeElster(formData.steuernummer ?? '', formData.plz ?? '', formData.bundesland ?? undefined) || null }]
      : []),
  ];

  // Die komplette De-minimis-Tabelle (colMinimi[n].*) hat im echten BAFA-Formular
  // ebenfalls kein <label> — alle Felder hier per DOM-id statt Label-Text. "Art" (Förder-
  // summe/Subventionswert) hat keine Entsprechung in unseren Daten; fest auf "Fördersumme"
  // gesetzt (Nutzerentscheid) — perspektivisch soll das im Bericht auswählbar werden.
  // Zeile 0 existiert immer; jede weitere Beihilfe braucht vorher einen Klick auf den
  // "Zeile hinzufügen"-Button (legt colMinimi1.*, colMinimi2.* usw. erst im DOM an —
  // per Diagnose am echten Formular bestätigt, selbe Feld-ids wie Zeile 0 mit Index n).
  const deminimisFelder: FillField[] = formData.deminimis.flatMap((eintrag, i) => {
    if (!eintrag) return [];
    const row: FillField[] = [
      { label: 'Datum des Bescheids', value: eintrag.datum, id: `colMinimi${i}.datum` },
      { label: 'Zuwendungsstelle', value: eintrag.stelle, id: `colMinimi${i}.zuwendung` },
      { label: 'Aktenzeichen', value: eintrag.aktenzeichen, id: `colMinimi${i}.aktenzeichen` },
      { label: 'Art', value: 'Fördersumme', id: `colMinimi${i}.art` },
      { label: 'Betrag', value: stripEuroFormat(eintrag.betrag), id: `colMinimi${i}.betrag` },
    ];
    if (i === 0) return row;
    return [{ label: 'Zeile hinzufügen', value: 'x', kind: 'button' as const }, ...row];
  });

  return [
    radioAendern,
    ...beratungsunternehmenFelder,
    ...durchfuehrendePersonFelder,
    ...beratungFelder,
    ...kostenFelder,
    ...bankFelder,
    ...personentypFelder,
    ...deminimisFelder,
  ];
}

/**
 * Seite 3 des VN-Formulars (ESF-Grundsätze-Fragebogen, teilt sich die URL mit Seite 2 —
 * Unterscheidung über den Feldinhalt, siehe VnFormPage.tsx). Nur Felder, zu denen `esfSelections`
 * (bereits gespeicherte Antworten) eine Auswahl enthält, werden befüllt — fehlt sie, bleibt die
 * Frage für die manuelle Beantwortung offen (wie sonst auch: nichts wird geraten, siehe
 * `fillFields`-Rückgabe `missed`). Checkboxen/Radios haben im echten Formular kein <label> —
 * deshalb immer per `bafaId` (DOM-id) statt Label-Text, analog personTyp/colMinimi oben.
 */
export function buildVnPage3EsfFelder(formData: VnFormData): FillField[] {
  const selected = new Set(formData.esfSelections ?? []);
  if (selected.size === 0) return [];
  return ESF_ALL_QUESTIONS.flatMap((frage) =>
    frage.options
      .filter((o) => selected.has(o.name))
      .map((o) => ({
        label: `${frage.label} — ${o.label}`,
        value: 'x',
        kind: (frage.radio ? 'radio' : 'checkbox') as const,
        id: o.bafaId,
      })),
  );
}
